import { isNil, round } from 'lodash';

import { AppConstants } from '../../../constants';
import { IContractTerm, IContractTermDisplay, IPresetContractTemplate } from '../../../interfaces';

const { CENT, INTEREST_RATE_DECIMAL_PLACES } = AppConstants;

// The band a template allows, as percentages so it can be compared against the rates the table
// shows. Null at either end is no bound there.
interface IRateBand {
  maximum: null | number;
  minimum: null | number;
}

const NO_RATE_BAND: IRateBand = { maximum: null, minimum: null };

// Rates are stored as a fraction and shown as a percentage, as they are on the contract modal. Shared
// so that the loan a details view reads back shows the same rates the modal that created it did.
function termsAsPercentages (terms: IContractTerm[]): IContractTermDisplay[] {
  return terms.map(term => ({
    ...term,
    interest_rate: round(Number(term.interest_rate) * CENT, INTEREST_RATE_DECIMAL_PLACES),
  }));
}

function asPercentage (rate: null | string): null | number {
  return isNil(rate) ? null : round(Number(rate) * CENT, INTEREST_RATE_DECIMAL_PLACES);
}

function rateBandOf (template: null | IPresetContractTemplate): IRateBand {
  if (!template) { return NO_RATE_BAND; }

  return {
    maximum: asPercentage(template.maximum_interest_rate),
    minimum: asPercentage(template.minimum_interest_rate),
  };
}

// Both ends are inclusive, as they are in LoanSerializer.validate.
function isRateOutOfBand (rate: number, band: IRateBand): boolean {
  return (!isNil(band.minimum) && rate < band.minimum) || (!isNil(band.maximum) && rate > band.maximum);
}

function describeRateBand (band: IRateBand): null | string {
  const { maximum, minimum } = band;

  if (!isNil(minimum) && !isNil(maximum)) { return `Rate must be between ${minimum}% and ${maximum}%`; }
  if (!isNil(minimum)) { return `Rate must be at least ${minimum}%`; }
  if (!isNil(maximum)) { return `Rate must be at most ${maximum}%`; }

  return null;
}

export { describeRateBand, isRateOutOfBand, rateBandOf, termsAsPercentages, IRateBand, NO_RATE_BAND };
